import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Cart, LineItem } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { Category, Variant } from 'shared/types/product';
import { ProductTypeKey } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { finishUpchargeCents } from 'shared/types/utils/finishUpcharge';
import Button from 'components/commercetools-ui/atoms/button';
import type { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import QuantitySelector from 'components/commercetools-ui/atoms/quantity-selector';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import GalleryMosaic from 'components/commercetools-ui/organisms/gallery/gallery-mosaic';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import {
  CART_LINE_ITEM_ID_QUERY_PARAM,
  CART_ITEM_QUANTITY_QUERY_PARAM,
  WIREBRUSH_QUERY_PARAM,
} from 'helpers/constants/query-params';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { useSetQueryParam } from 'helpers/hooks/useSetQueryParam';
import { variantDisplayNames } from 'helpers/seo/variant-seo';
import { Reference } from 'types/reference';
import AdditionalInfo from './components/additional-info';
import ProductInformation from './components/product-information';
import StickyBuyBar from './components/sticky-buy-bar';
import { hasCustomQuoteCta } from './helpers';
import { getColorSwatch } from './helpers/colorSwatch';
import { findMatchingLineItem } from './helpers/findMatchingLineItem';
import { compatibleFinishOptions, groupFinishOptions } from './helpers/finishGroups';
import {
  GALLERY_START,
  GalleryPosition,
  composeGalleryEntries,
  describeGalleryIndex,
  galleryImagesKey,
  resolveGalleryIndex,
} from './helpers/galleryPosition';
import { getVariantSpeciesAttribute, toTagSlug } from './helpers/getVariantSpecies';
import { sortAxisValues } from './helpers/optionAxes';
import { getProductVideo } from './helpers/productVideos';
import { useFinishAndSheens } from './hooks/useFinishAndSheens';
import { useProductFinishImages } from './hooks/useProductFinishImages';
import { UIProduct, UIProductContent } from './types';
import { FrontasticImage } from '../../../atoms/image';
import { ReviewListProps } from '../../review-list';
import { customQuoteLink } from '../../product/product-list/context/constants';

// The sticky buy bar's height until it has measured itself (--buy-bar-reserve): once shown, the bar
// only hides after the price block is back by more than this, so the bar's own appearance can't flip
// the check back and forth.
const STICKY_BAR_ALLOWANCE = 72;
// "…/upload/v1774616040/www_viewrail/products/x/rnze6ksrqjfwvqsq0har.jpg" → "rnze6ksrqjfwvqsq0har".
const publicIdOf = (url?: string) => url?.split('?')[0].split('/').pop()?.replace(/\.[a-z0-9]+$/i, '');
// Must match Tailwind's `lg` screen: the mosaic's desktop layout, and so its sticky alignment.
const DESKTOP_QUERY = '(min-width: 1025px)';

const getQuantityFromParam = (quantityParam: string | null) => {
  const quantity = Number(quantityParam);
  return Number.isInteger(quantity) && quantity > 0 ? quantity : undefined;
};

export interface ProductDetailsProps {
  product: UIProduct;
  productContent?: UIProductContent;
  variant: ExtendedVariant;
  url?: string;
  category?: Category;
  inModalVersion?: boolean;
  /** Server's device guess (phones/tablets): the gallery renders its carousel first. */
  phoneLayout?: boolean;
  woodFinishHelper?: {
    text?: string;
    linkLabel?: string;
    link?: Reference;
  };
  wirebrushModal?: {
    title: string;
    text: string;
    images?: Array<{ name: string; image: FrontasticImage }>;
    closeButtonAriaLabel?: string;
  };
  onChangeVariant: (sku: string) => void;
  description?: ReactNode;
  plainTextDescription?: string;
  origin?: string;
  setIsOpen?: (value: boolean) => void;
  removeLineItem?: (item: LineItem & { customLineItemId?: string }) => Promise<void>;
  updateLineItem?: (
    lineItemId: string,
    customLineItemId: string | undefined,
    quantity: number,
  ) => Promise<{ isError: boolean; data?: Cart; error?: unknown }>;
  editLineItem?: ExtendedLineItem;
  onAddToCart?: (
    variant: Variant,
    quantity: number,
    finish?: string,
    sheen?: string,
    hasWirebrush?: boolean,
    options?: { skipAnalytics?: boolean },
  ) => Promise<Cart>;
  isCartItemEditPending?: boolean;
  inCartQuantity?: number;
  reviewListProps?: ReviewListProps;
}

const ProductDetails: FC<ProductDetailsProps> = ({
  product,
  productContent,
  variant,
  category,
  url,
  description,
  plainTextDescription,
  inModalVersion,
  phoneLayout,
  woodFinishHelper,
  wirebrushModal,
  onChangeVariant,
  setIsOpen,
  removeLineItem,
  updateLineItem,
  editLineItem,
  isCartItemEditPending = false,
  onAddToCart,
  reviewListProps,
  origin,
  // inCartQuantity = 0,
}) => {
  const { formatMessage } = useFormat({ name: 'cart' });
  const router = useRouter();
  const searchParams = useSearchParams();

  // Desktop only and batched to one measure per frame: below `lg` nothing is sticky.
  const mosaicActive = !inModalVersion && (product.lifestyleImages?.length ?? 0) > 0;
  // Desktop mosaic scroll: whichever column is shorter pins while the other scrolls past it — the
  // right column (configurator, then description) while the lifestyle photos scroll, or the medium
  // tiles while a long right column scrolls — then the section scrolls on together. The section keeps
  // its natural height: stretching it past its tallest column left a stretch where both columns were
  // pinned and nothing on screen moved. The right column's height sets its sticky top: one taller than
  // the screen scrolls to its end before pinning, so nothing is cut off.
  const mosaicRightRef = useRef<HTMLDivElement>(null);
  const [mosaicScroll, setMosaicScroll] = useState<{ right: number } | null>(null);
  useEffect(() => {
    const right = mosaicRightRef.current;
    if (!mosaicActive || !right || typeof ResizeObserver === 'undefined') {
      setMosaicScroll(null);
      return;
    }
    const desktop = window.matchMedia(DESKTOP_QUERY);
    let frame = 0;
    const measure = () => {
      if (!desktop.matches) {
        setMosaicScroll(null);
        return;
      }
      const next = { right: right.offsetHeight };
      // Returning the current object for unchanged values avoids a re-render per resize callback.
      setMosaicScroll((current) => (current?.right === next.right ? current : next));
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(right);
    desktop.addEventListener('change', scheduleMeasure);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      desktop.removeEventListener('change', scheduleMeasure);
    };
  }, [mosaicActive]);

  const initialQuantity = editLineItem?.count ?? getQuantityFromParam(searchParams.get(CART_ITEM_QUANTITY_QUERY_PARAM)) ?? 1;
  const hasCartItemEditQuery = searchParams.get(CART_LINE_ITEM_ID_QUERY_PARAM) !== null;

  const [quantity, setQuantity] = useState<number>(initialQuantity);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  // §8: an option combination with no variant behind it must block the add rather than fall back to
  // a near-match. The configurator owns the selection, so it reports the match state up to here.
  const [hasMatchingVariant, setHasMatchingVariant] = useState(true);
  const hasSyncedInitialQuantity = useRef(false);

  useEffect(() => {
    if (hasSyncedInitialQuantity.current) return;
    if (hasCartItemEditQuery && !editLineItem) return;

    setQuantity(initialQuantity);
    hasSyncedInitialQuantity.current = true;
  }, [editLineItem, hasCartItemEditQuery, initialQuantity]);

  const { finish, finishKey, finishes, hasUnresolvedCartFinish, sheen, sheens, onChangeFinish, onChangeSheen } =
    useFinishAndSheens(product, variant);
  const speciesTag = getVariantSpeciesAttribute(variant);
  // Products sold in colors as separate SKUs (Express) have no finish picker, but their colors are the
  // same powder coats as the finish swatches, so they get the swatch tile too.
  // Only the mosaic has a swatch tile, so the classic gallery never fetches (or shows) these images.
  const colorSwatch = mosaicActive ? getColorSwatch(product, variant, (finishes?.length ?? 0) > 0) : undefined;
  const {
    skuImages: finishImages,
    speciesImages: speciesFinishImages,
    genericImages: genericFinishImages,
  } = useProductFinishImages(
    variant.sku,
    finishKey ?? colorSwatch?.finishKey,
    speciesTag ? toTagSlug(speciesTag) : undefined,
  );

  // Finish images tagged to this SKU take over the gallery; the base `slug:`/`product_id:`
  // images stand in when there are none. The two shared tiers — species+finish (wood stains)
  // then finish-only (powder coat) — always rank last, so they supplement rather than
  // displace, most specific first.
  const baseImages = product.images.length ? product.images : (variant.images?.map((img: string) => ({ src: img })) ?? []);
  const galleryEntries = composeGalleryEntries({
    product: finishImages.length ? finishImages : baseImages,
    species: speciesFinishImages,
    generic: genericFinishImages,
  });
  const galleryImages = galleryEntries.map((entry) => entry.image);

  // Mosaic major image + its tray: the parent's lifestyle photos (`lifestyle:<slug>`), which never
  // change with the variant. A product with none keeps the classic gallery (Joel, 3 Oct 2026): the
  // mosaic only earns its layout when there is a real lifestyle photo to lead with.
  const lifestyleImages = product.lifestyleImages ?? [];
  const productVideo = getProductVideo(product.slug);

  // The species+finish (wood) or finish-only (color) image is the finish's swatch; the mosaic gives
  // it its own tile.
  const swatchImage = speciesFinishImages[0] ?? genericFinishImages[0];
  // A color with no finish photo (DriveTite: Onyx Black / Brushed Stainless) uses each color variant's
  // own product shot instead — its first commercetools image, found in the gallery by Cloudinary public
  // id so the tile and viewer share one image object. The chips then swap the tile between the shots.
  const colorShot = (sku: string) => {
    const id = publicIdOf(product.variants.find((candidate) => candidate.sku === sku)?.images?.[0]);
    return id ? galleryImages.find((image) => publicIdOf(image.media?.mediaId ?? image.src) === id) : undefined;
  };
  const colorShotSwatch =
    colorSwatch && !swatchImage
      ? {
          image: colorShot(variant.sku),
          siblings: colorSwatch.options
            .filter((option) => option.sku !== variant.sku)
            .map((option) => colorShot(option.sku))
            .filter((image): image is ImageProps => Boolean(image)),
        }
      : undefined;

  // Changing the finish rebuilds the gallery and remounts it, which would otherwise drop the
  // viewer back on the primary image. Remember the slide by tier so the equivalent image is
  // shown for the new finish: someone comparing the shared finish photo across finishes stays
  // on that photo, and someone on the third product shot stays on the third product shot.
  // Only the viewer's own moves update this — Gallery does not report on mount, so a remount
  // that lands on a fallback slide can never overwrite what the viewer chose.
  const [galleryPosition, setGalleryPosition] = useState<GalleryPosition>(GALLERY_START);
  const onGalleryIndexChange = (index: number) => {
    const position = describeGalleryIndex(galleryEntries, index);
    setGalleryPosition((current) =>
      current.tier === position.tier && current.offset === position.offset ? current : position,
    );
  };

  const { setQueryParam } = useSetQueryParam();

  const [hasWirebrush, setHasWirebrush] = useState<boolean | undefined>(() => {
    if (!product.useWirebrush) return undefined;
    const wirebrushParam = searchParams.get(WIREBRUSH_QUERY_PARAM);
    if (wirebrushParam !== null) return wirebrushParam === 'true';
    // handrail types show wirebrush but default to unchecked
    const defaultCheckedTypes: ProductTypeKey[] = ['wood-stair-parts-treads', 'wood-tread-species-dropdown'];
    return product.type !== undefined && defaultCheckedTypes.includes(product.type) ? true : undefined;
  });

  const onChangeWirebrush = product.useWirebrush
    ? (isWirebrushChecked: boolean) => {
      setHasWirebrush(isWirebrushChecked);
      setQueryParam(WIREBRUSH_QUERY_PARAM, String(isWirebrushChecked));
    }
    : undefined;

  // Eagerly write the wirebrush default to the URL on mount so that sharing or
  // bookmarking a tread PDP captures the default wirebrush=true state.
  useEffect(() => {
    if (hasWirebrush !== undefined && searchParams.get(WIREBRUSH_QUERY_PARAM) === null) {
      setQueryParam(WIREBRUSH_QUERY_PARAM, String(hasWirebrush));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { show } = useAddToCartOverlay();
  const isEditingCartItem = !!editLineItem?.lineItemId;
  const isAddToCartDisabled = quantity === 0 || isCartItemEditPending || hasUnresolvedCartFinish || !hasMatchingVariant;

  const isSameCartConfiguration = () => {
    if (!editLineItem) return false;

    const normalizeWirebrush = (value: unknown) => {
      if (value === undefined || value === null) return undefined;
      return String(value) === 'true';
    };
    const normalizeConfigValue = (value: unknown) => {
      if (value === undefined || value === null || value === '') return undefined;
      return String(value);
    };

    return (
      editLineItem.variant?.sku === variant.sku &&
      normalizeConfigValue(editLineItem.config?.finish) === normalizeConfigValue(finish?.name) &&
      normalizeConfigValue(editLineItem.config?.sheen) === normalizeConfigValue(sheen) &&
      normalizeWirebrush(editLineItem.config?.wirebrush) === normalizeWirebrush(hasWirebrush)
    );
  };

  const handleUpdateCartItem = async () => {
    if (!editLineItem?.lineItemId) return;

    if (isSameCartConfiguration() && updateLineItem) {
      const result = await updateLineItem(editLineItem.lineItemId, editLineItem.customLineItemId, quantity);
      if (result.isError) throw result.error;
    } else if (editLineItem.variant?.sku === variant.sku && removeLineItem) {
      await onAddToCart?.(variant, quantity, finish?.name, sheen, hasWirebrush, { skipAnalytics: true });
      await removeLineItem(editLineItem);
    } else {
      await onAddToCart?.(variant, quantity, finish?.name, sheen, hasWirebrush, { skipAnalytics: true });
      if (removeLineItem) await removeLineItem(editLineItem);
    }

    router.push('/cart');
  };

  const handleAddToCart = async () => {
    if (isAddToCartDisabled) {
      return;
    }

    setLoading(true);

    try {
      if (isEditingCartItem) {
        await handleUpdateCartItem();
        setLoading(false);
        setAdded(true);
        return;
      }

      const cart = await onAddToCart?.(variant, quantity, finish?.name, sheen, hasWirebrush);
      setLoading(false);
      setAdded(true);

      const matchingItem = cart
        ? findMatchingLineItem(cart, variant, {
          finish,
          sheen,
          hasWirebrush,
        })
        : null;

      const rawBasePrice = variant.price?.centAmount || 0;
      const rawDiscountedPrice = variant.discountedPrice?.centAmount || 0;

      // Calculate discount factor (e.g. 0.8 for 20% off)
      const discountFactor =
        rawBasePrice > 0 && rawDiscountedPrice > 0 ? rawDiscountedPrice / rawBasePrice : 1;

      const finishPrice = finish?.price || 0;
      // finish.price is the list-price upcharge; scale it to the sale price through the shared
      // helper so the rounding matches every other surface that shows this upcharge.
      const discountedFinishPrice = finishUpchargeCents(finishPrice, finish?.name, 1, discountFactor);

      // Create a cloned variant with updated prices for the overlay only
      const displayVariant = {
        ...variant,
        price: variant.price
          ? {
            ...variant.price,
            centAmount: rawBasePrice + finishPrice,
          }
          : undefined,
        discountedPrice: variant.discountedPrice
          ? {
            ...variant.discountedPrice,
            centAmount: rawDiscountedPrice + discountedFinishPrice,
          }
          : undefined,
      };

      show(product, displayVariant as ExtendedVariant, quantity, {
        // Hand the overlay the exact line we just resolved — its own SKU + config search cannot
        // tell two externally-configured lines apart.
        lineItemId: matchingItem?.lineItemId,
        customLineItemId: matchingItem?.customLineItemId,
        config: matchingItem?.config || {
          finish: finish?.name,
          sheen,
          wirebrush: hasWirebrush,
        },
      });

      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);
    } catch (error) {
      console.error('Error adding item to cart:', error);
      setLoading(false);
    }
  };

  const wrapperClassName = inModalVersion
    ? 'md:grid grid-cols-12 pt-70 pb-35 container'
    : 'container pt-16 pb-32 grid gap-y-32 md:gap-x-32 grid-cols-12 items-start';

  // Gallery and configurator split the row evenly, so the thumbnail rail plus the main image
  // occupy half the width and the configurator gets the other half.
  // self-stretch makes this container fill the full grid height, enabling sticky scroll within it
  const galleryContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6 md:mt-32' : 'col-span-12 md:col-span-6 lg:col-span-6 lg:row-span-2 lg:self-stretch',
  ]);

  const informationContainerClassName = useClassNames([
    // Brand-refresh: the whole info column uses the GT typeface + brand ink so the legacy body
    // matches the surrounding brand chrome.
    'font-gt text-[#212121]',
    inModalVersion ? 'col-span-6 mt-24 md:mt-32' : 'col-span-12 md:col-span-6 lg:col-span-6',
  ]);

  const categoryHasCustomQuoteCta = hasCustomQuoteCta(product?.categories || []);

  const ctaContainerClassName = useClassNames([
    'mt-24 flex gap-16',
    categoryHasCustomQuoteCta ? 'flex-wrap' : '',
  ]);

  // Lock the AdditionalInfo container's height so the sticky gallery image
  // doesn't jump when its accordion panels expand/collapse.  At the `lg`
  // breakpoint the gallery uses self-stretch + row-span-2, so its height
  // equals row-1 + row-2.  Locking row-2's min-height (via AdditionalInfo)
  // prevents the grid from redistributing row heights when accordion content
  // changes.
  //
  // The ratchet intentionally only grows — never shrinks.  Releasing min-height
  // (even with a CSS transition) changes the grid row height, which repositions
  // the sticky gallery.  The minor dead space after collapsing a tall accordion
  // is an acceptable tradeoff for a stable image position.
  const additionalInfoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = additionalInfoRef.current;
    if (!el || inModalVersion) return;

    // Must match Tailwind's `lg:` breakpoint where the 2-column grid layout applies
    const mql = window.matchMedia('(min-width: 1024px)');
    let maxHeight = 0;

    const observer = new ResizeObserver((entries) => {
      const height = entries[0].borderBoxSize?.[0]?.blockSize ?? el.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
        el.style.minHeight = `${height}px`;
      }
    });

    const activate = () => {
      maxHeight = el.offsetHeight;
      el.style.minHeight = `${maxHeight}px`;
      observer.observe(el);
    };

    const deactivate = () => {
      maxHeight = 0;
      el.style.minHeight = '';
      observer.disconnect();
    };

    if (mql.matches) activate();

    const handleBreakpointChange = () => {
      if (mql.matches) activate();
      else deactivate();
    };
    mql.addEventListener('change', handleBreakpointChange);

    return () => {
      deactivate();
      mql.removeEventListener('change', handleBreakpointChange);
    };
  }, [inModalVersion, product.productId]);

  const rawBasePrice = variant.price?.centAmount || 0;
  const rawDiscountedPrice = variant.discountedPrice?.centAmount || 0;
  const discountFactor =
    rawBasePrice > 0 && rawDiscountedPrice > 0 ? rawDiscountedPrice / rawBasePrice : 1;

  // Sticky buy bar: reveal it only once the real Add to Cart button has scrolled up behind the
  // sticky header, so the bar never covers the gallery while the button is still on screen. Measured
  // against the visible header's height because the header overlays the top of the viewport.
  const priceSentinelRef = useRef<HTMLDivElement>(null);
  const [showStickyBuyBar, setShowStickyBuyBar] = useState(false);
  useEffect(() => {
    const sentinel = priceSentinelRef.current;
    if (!sentinel || inModalVersion) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const header = [...document.querySelectorAll('header')].find((el) => el.getBoundingClientRect().height > 0);
      const headerBottom = header?.getBoundingClientRect().bottom ?? 0;
      const bottom = sentinel.getBoundingClientRect().bottom;
      // Hysteresis: once shown, the bar only hides after the price block is back by more than the
      // bar's height, so a price block resting right at the header's edge can't flip it every frame.
      // (Pinned columns reserve the bar's room up front, so the bar appearing no longer moves them.)
      const reserve =
        parseFloat(document.documentElement.style.getPropertyValue('--buy-bar-reserve')) || STICKY_BAR_ALLOWANCE;
      setShowStickyBuyBar((shown) => (shown ? bottom < headerBottom + reserve : bottom < headerBottom));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
    // Re-subscribes when the sentinel mounts late — a first variant with no price, then a switch to one.
  }, [inModalVersion, variant.price?.centAmount]);

  const stickyFinishCents = finish?.price || 0;
  const stickyPriceCents =
    rawDiscountedPrice > 0
      ? rawDiscountedPrice + finishUpchargeCents(stickyFinishCents, finish?.name, 1, discountFactor)
      : rawBasePrice + stickyFinishCents;
  const stickyListCents = rawBasePrice + stickyFinishCents;

  return (
    <>
      {!inModalVersion && variant.price && (
        <StickyBuyBar
          visible={showStickyBuyBar}
          productName={variantDisplayNames(product).get(variant.sku) ?? variant.name ?? product.name}
          price={{ ...variant.price, centAmount: stickyPriceCents }}
          listPrice={
            rawDiscountedPrice > 0 && stickyListCents > stickyPriceCents
              ? { ...variant.price, centAmount: stickyListCents }
              : undefined
          }
          ctaLabel={formatMessage({ id: 'cart.add', defaultMessage: 'Add to cart' })}
          disabled={isAddToCartDisabled}
          loading={loading}
          added={added}
          onAddToCart={handleAddToCart}
        />
      )}

      <div className={wrapperClassName}>
        <div className={galleryContainerClassName}>
          {/* Mosaic: lg:h-full lets its medium group stay pinned for the whole section. Classic gallery: the whole gallery is sticky, as before. */}
          <div
            className={
              mosaicActive
                ? 'lg:h-full'
                : inModalVersion
                  ? ''
                  : 'lg:sticky lg:top-[calc(var(--header-height,0px)+var(--buy-bar-reserve,0px))]'
            }
          >
            {/* eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values -- Full-bleed layout pattern */}
            <div className={`relative inset-x-1/2 -mx-[50vw] w-screen md:static md:mx-0 md:w-auto${mosaicActive ? ' lg:h-full' : ''}`}>
              {!mosaicActive ? (
                <Gallery
                  key={galleryImagesKey(galleryEntries)}
                  images={galleryImages}
                  initialSlide={resolveGalleryIndex(galleryEntries, galleryPosition)}
                  onActiveIndexChange={onGalleryIndexChange}
                  inModalVersion={inModalVersion}
                  enableImageModal
                />
              ) : (
                <GalleryMosaic
                  initialPhoneLayout={phoneLayout}
                  lifestyles={lifestyleImages}
                  video={productVideo}
                  images={galleryImages}
                  initialSlide={resolveGalleryIndex(galleryEntries, galleryPosition)}
                  onActiveIndexChange={onGalleryIndexChange}
                  swatch={
                    finishes?.length
                      ? {
                          image: swatchImage,
                          hex: finish?.hex,
                          name: finish?.name,
                          // Same finishes, order and grouping as the buy-box picker, so the two read as one control.
                          options: groupFinishOptions(
                            sortAxisValues(
                              'finish',
                              compatibleFinishOptions(
                                finishes.map(({ name, hex }) => `${name}:${hex}`),
                                variant.attributes?.material as string | undefined,
                              ),
                            ),
                            product.isWoodProduct ?? false,
                          )
                            .flatMap((group) => group.options)
                            .map((option) => {
                              const [name, hex] = option.split(':');
                              return { name, hex: hex ?? null };
                            }),
                          onSelect: onChangeFinish,
                        }
                      : // A color with no swatch photo (e.g. a non-powder-coat "Bronze") keeps the product image in
                        // Medium B rather than a bare color square.
                        colorSwatch && (swatchImage || colorShotSwatch?.image)
                        ? {
                            image: swatchImage ?? colorShotSwatch?.image,
                            siblingImages: colorShotSwatch?.siblings,
                            hex: colorSwatch.hex ?? undefined,
                            name: colorSwatch.name,
                            options: colorSwatch.options.map(({ name, hex }) => ({ name, hex })),
                            onSelect: (name) => {
                              const sku = colorSwatch.options.find((option) => option.name === name)?.sku;
                              if (sku) onChangeVariant(sku);
                            },
                          }
                        : undefined
                  }
                />
              )}
            </div>
          </div>
        </div>
        {/* Below desktop this wrapper is `contents`, so the configurator and description stay separate
            grid items (the description goes full width under the gallery on tablets and phones). On a
            desktop mosaic it is the pinned right column described at mosaicScroll. */}
        <div
          ref={mosaicRightRef}
          className={
            mosaicActive
              ? 'contents lg:sticky lg:col-span-6 lg:col-start-7 lg:row-span-2 lg:flex lg:flex-col lg:gap-32 lg:self-start'
              : 'contents'
          }
          style={
            mosaicScroll
              ? {
                  top: `min(calc(var(--header-height,0px) + var(--buy-bar-reserve,0px)), calc(100vh - ${mosaicScroll.right}px - 16px))`,
                }
              : undefined
          }
        >
        <div className={informationContainerClassName}>
          {/* Sticky makes this a stacking context, trapping the option dropdown's z-50. Without its own
              z-index, the later "Product description" title (Typography is always `relative`) paints
              over the open menu. Stays under the buy bar (z-40) and header (z-50). */}
          <div
            className={
              inModalVersion
                ? undefined
                : 'lg:sticky lg:top-[calc(var(--header-height,0px)+var(--buy-bar-reserve,0px))] lg:z-10'
            }
          >
          <ProductInformation
            reviewListProps={reviewListProps}
            product={product}
            variant={variant}
            plainTextDescription={plainTextDescription}
            category={category}
            onChangeVariant={onChangeVariant}
            finish={finish}
            wirebrushModal={wirebrushModal}
            finishOptions={finishes?.map((finishObj) => `${finishObj.name}:${finishObj.hex}`)}
            sheen={sheen}
            sheenOptions={sheens}
            onChangeFinish={onChangeFinish}
            onChangeSheen={onChangeSheen}
            hasWirebrush={hasWirebrush}
            onChangeWirebrush={onChangeWirebrush}
            inModalVersion={inModalVersion}
            woodFinishHelper={woodFinishHelper}
            removeLineItem={removeLineItem}
            origin={origin}
            extraCentAmount={finish?.price}
            discountFactor={discountFactor}
            onSelectionMatchChange={setHasMatchingVariant}
            bnplAmount={
              (rawDiscountedPrice > 0
                ? rawDiscountedPrice + finishUpchargeCents(finish?.price || 0, finish?.name, 1, discountFactor)
                : rawBasePrice + (finish?.price || 0)) * quantity
            }
          />

          {!hasMatchingVariant && (
            <p role="alert" className="mt-24 border border-[#d91e28] px-16 py-10 text-14 text-[#d91e28]">
              That combination isn&apos;t available — try a different option.
            </p>
          )}

          <div className={ctaContainerClassName}>
            {variant.price?.centAmount ? (
              <>
                {categoryHasCustomQuoteCta ? (
                  <>
                    {/* Custom Quote CTA */}
                    <Link
                      buttonVariant="primary"
                      className="font-medium"
                      link={customQuoteLink}
                      target="_blank"
                    >
                      Custom Quote
                    </Link>
                    <Button
                      className="whitespace-nowrap font-medium hover:underline"
                      variant="ghost"
                      size="xs"
                      onClick={handleAddToCart}
                      loading={loading}
                      added={added}
                      disabled={isAddToCartDisabled}
                    >
                      Buy as Pictured &gt;
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Add to cart CTA */}
                    {/* Since there's no tracking of stock, we use a really high value as the maximum */}
                    <QuantitySelector className="shrink-0" value={quantity} minValue={1} maxValue={999} onChange={setQuantity} />
                    <Button
                      className="!h-52 !w-auto min-w-0 flex-1 basis-0 !whitespace-nowrap !rounded-none !bg-[#212121] !px-16 !text-14 !font-medium !uppercase !tracking-[0.08em] !text-white hover:!opacity-90"
                      variant="secondary-black"
                      onClick={handleAddToCart}
                      loading={loading}
                      added={added}
                      disabled={isAddToCartDisabled}
                    >
                      {isCartItemEditPending || hasUnresolvedCartFinish
                        ? formatMessage({ id: 'cart.item.loading', defaultMessage: 'Loading cart item...' })
                        : isEditingCartItem
                        ? formatMessage({ id: 'cart.update', defaultMessage: 'Update cart' })
                        : formatMessage({ id: 'cart.add', defaultMessage: 'Add to cart' })}
                    </Button>
                  </>
                )}
                {/* Watched by the sticky buy bar: the bar shows once this line is behind the header. Sits
                    outside the custom-quote branch so FLIGHT PDPs get the bar too. */}
                {!inModalVersion && <div ref={priceSentinelRef} aria-hidden="true" className="h-px w-0 shrink-0" />}
              </>
            ) : (
              <div className="flex flex-col gap-y-8">
                <p className="body-text font-medium text-error">
                  Product currently unavailable, please contact our Sales team.
                </p>
                <Link buttonVariant="secondary" className="flex justify-center" link={'/contact-us'} target="_blank">
                  Contact Us
                </Link>
              </div>
            )}
          </div>

          {inModalVersion && (
            <Link
              link={url}
              className="mx-auto mt-28 block w-fit border-b border-transparent text-center text-14 leading-loose text-secondary-black hover:border-secondary-black"
              onClick={() => setIsOpen?.(false)}
            >
              {formatMessage({ id: 'more.details', defaultMessage: 'More details' })}
            </Link>
          )}
          </div>
        </div>
        {/* Tablets get the description full width below gallery + configurator, as phones do; desktop keeps it under the configurator. */}
        {!inModalVersion && (
          <div
            ref={additionalInfoRef}
            className="col-span-12 lg:col-span-6 lg:col-start-7"
          >
            <AdditionalInfo {...productContent} description={description} />
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ProductDetails;
